import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EventTemplateLayout } from '@models/design-templates/event-template-layout.enum';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { RSVPOptionType } from '@models/event-attendance/rsvp-option-type.enum';
import { CreateOrUpdateEventConfirmationPageCardRequest } from '@models/events/dto/create-or-update-event-confirmation-page-card.request';
import { EventConfirmationPageCardType } from '@models/events/enums/event-confirmation-page-card-type.enum';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { EventConfirmationPageCard } from '@models/events/event-confirmation-page-card.enum';
import { EventRSVPConfirmationPageSectionType } from '@models/events/event-rsvp-confirmation-page-section-type.enum';
import { EventWidget } from '@models/events/event-widget.enum';
import { Event } from '@models/events/event.model';
import { UserProfileInfo } from '@models/users/dto/user-profile-info.response';
import { User } from '@models/users/user.model';
import { GenericTicketInfoSuccessComponent } from '@modules/events/components/generic-ticket-info-success/generic-ticket-info-success.component';
import { GetBoughtTicketsDialog } from '@modules/events/dialogs/get-bought-tickets/get-bought-tickets.dialog';
import { RsvpQrCodeDialog } from '@modules/events/dialogs/rsvp-qr-code/rsvp-qr-code.dialog';
import { RichTextEditorComponent } from '@modules/shared/components/rich-text-editor/rich-text-editor.component';
import { ShareButtonsComponent } from '@modules/shared/components/share-buttons/share-buttons.component';
import { LogoCustomFillComponent } from '@modules/shared/logo-custom-fill/logo-custom-fill.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { UserService } from '@services/auth/user.service';
import { EventService } from '@services/events/event.service';
import { NotificationService } from '@services/shared/notification.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { debounceTime, finalize, Subject } from 'rxjs';
import { TextEditableDirective } from '../../../../../../directives/text-editable.directive';
import { SafeHtmlPipe } from '../../../../../../pipes/safe-html.pipe';
import { AppRoutes } from '../../../../../../routes';

@Component({
  selector: 'app-generic-after-attend-event-card',
  standalone: true,
  imports: [
    TranslateModule,
    SafeHtmlPipe,
    GenericTicketInfoSuccessComponent,
    ShareButtonsComponent,
    LogoCustomFillComponent,
    RichTextEditorComponent,
    FormsModule,
    TextEditableDirective,
  ],
  templateUrl: './generic-after-attend-event-card.component.html',
  styleUrl: './generic-after-attend-event-card.component.scss',
})
export class GenericAfterAttendEventCardComponent implements OnInit {
  @Input() withTickets?: boolean = false;
  @Input() withSlots?: boolean = false;
  @Input() eventConfirmationPage?: EventConfirmationPageCard;
  @Input() attendee?: EventAttendee;
  @Input() ticketOrder?: EventAttendeeTicketOrder;
  @Input() myOrders?: EventAttendeeTicketOrder[];
  @Input() isEdit?: boolean = false;
  loggedUser?: User;
  profileInfo?: UserProfileInfo;
  isDesktop: boolean = true;

  @Output() close = new EventEmitter();
  @Output() refreshData = new EventEmitter();
  actionInProgress?: 'addToWallet';
  private _rsvpType?: RSVPOptionType;
  title: string = '';
  description: string = '';
  private _confirmationPageCard?: EventConfirmationPageCard;
  private _event?: Event;
  private titleSubject = new Subject<string>();
  private descriptionSubject = new Subject<string>();
  private ctaSubject = new Subject<string>();

  renderEditor = true;

  @Input()
  set event(value: Event | undefined) {
    this._event = value;
  }

  get event(): Event | undefined {
    return this._event;
  }

  @Input()
  set confirmationPageCard(value: EventConfirmationPageCard | undefined) {
    this._confirmationPageCard = value;
    this.renderEditor = false;
    setTimeout(() => (this.renderEditor = true)); // Let Angular destroy and re-render
  }

  get confirmationPageCard(): EventConfirmationPageCard | undefined {
    return this._confirmationPageCard;
  }

  @Input()
  set rsvpType(value: RSVPOptionType | undefined) {
    this._rsvpType = value;
    this.confirmationPageCard = this.findCardByType();
  }

  get rsvpType(): RSVPOptionType | undefined {
    return this._rsvpType;
  }

  encodedMainImage: Signal<string> = computed(() => {
    if (
      this.event &&
      (this.event.isGeneric() ||
        (this.event.type === 'WEDDINGPAGE' &&
          this.event.eventTemplate?.layout ===
            EventTemplateLayout.ONE_COLUMN_MODERN)) &&
      this.event.eventTemplate?.seo?.sharingPictureUrl
    ) {
      return this.event.eventTemplate?.seo?.sharingPictureUrl;
    }
    return this.event ? this.event.getEncodedMainImage() : '';
  });

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private eventService: EventService,
    private notificationService: NotificationService,
    private userService: UserService,
    private authService: AuthService,
    private screenWidthService: ScreenWidthService,
    private router: Router,
  ) {
    this.authService.userSubject
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this.loggedUser = user;
        if (user.id) {
          this.userService
            .getUserProfileInfo(user.id)
            .subscribe((profileInfo) => {
              this.profileInfo = profileInfo;
            });
        }
      });

    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => (this.isDesktop = isDesktop));
  }

  ngOnInit(): void {
    this.confirmationPageCard = this.findCardByType();
    this.titleSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.performSave('title', value);
    });

    this.descriptionSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.performSave('description', value);
    });

    this.ctaSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.performSave('ctaText', value);
    });
  }

  private performSave(type: string, content: string) {
    if (!this.event) return;

    const cardType = this.getEventConfirmationPageCardType();
    if (cardType) {
      const request: CreateOrUpdateEventConfirmationPageCardRequest = {
        type: cardType,
        [type]: content,
      };

      this.eventService
        .editConfirmationPageCard(this.event.id, request)
        .subscribe({
          next: (res) => {
            this._confirmationPageCard = res;
            if (this.event) {
              this.refreshData.emit();
            }
          },
        });
    }
  }

  onClose() {
    this.close.emit();
  }

  getHeaderStatus() {
    switch (this.rsvpType) {
      case RSVPOptionType.YES:
        return this.translate.instant('APP.EVENT_AFTER_ATTEND.GENERIC.SUCCESS');
      case RSVPOptionType.NO:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.NO_RESPONSE',
        );
      case RSVPOptionType.MAYBE:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.MAYBE_RESPONSE',
        );
      case RSVPOptionType.WAITLIST:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.WAIT_LIST_RESPONSE',
        );
      case RSVPOptionType.PENDING_APPROVAL:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.PENDING_RESPONSE',
        );
      case RSVPOptionType.WITHDRAW:
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.WITHDRAW_RESPONSE',
        );
      default:
        return '';
    }
  }
  saveEditorContent(valueObj: any, type: string) {
    if (type === 'title') {
      this.titleSubject.next(valueObj.value);
    } else if (type === 'description') {
      this.descriptionSubject.next(valueObj.value);
    } else if (type === 'ctaText') {
      this.ctaSubject.next(valueObj);
    }
  }

  getEventConfirmationPageCardType() {
    switch (this.rsvpType) {
      case RSVPOptionType.YES:
        return EventConfirmationPageCardType.YES;
      case RSVPOptionType.NO:
        return EventConfirmationPageCardType.NO;
      case RSVPOptionType.MAYBE:
        return EventConfirmationPageCardType.MAYBE;
      case RSVPOptionType.WAITLIST:
        return EventConfirmationPageCardType.WAITLIST;
      case RSVPOptionType.PENDING_APPROVAL:
        return EventConfirmationPageCardType.PENDING;
      case RSVPOptionType.WITHDRAW:
        return EventConfirmationPageCardType.WITHDRAW;
      default:
        return undefined;
    }
  }

  hasConfirmed() {
    return (
      this.rsvpType === RSVPOptionType.YES ||
      this.rsvpType === RSVPOptionType.MAYBE
    );
  }

  seeTickets(mode: string) {
    let ticket: EventAttendeeTicket | undefined;
    let order: EventAttendeeTicketOrder | undefined = this.ticketOrder;

    if (mode === 'user') {
      ticket = this.ticketOrder?.eventAttendeeTickets.find(
        (x) => x.eventAttendee?.userId === this.loggedUser?.id,
      );
    } else if (mode === 'allOrders') {
      order = undefined;
    }

    this.openGetBoughtTicketsDialog(ticket, order);
  }

  openGetBoughtTicketsDialog(
    ticket?: EventAttendeeTicket,
    order?: EventAttendeeTicketOrder,
  ) {
    const dialogRef = this.dialog.open(GetBoughtTicketsDialog, {
      maxWidth: '456px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        orders: this.myOrders,
        loggedUser: this.loggedUser,
        ticket: ticket,
        order: order,
      },
      panelClass: ['normal-dialog', 'new-normal-dialog'],
      backdropClass: '',
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.refresh) {
        this.refreshData.emit();
      }
    });
  }

  getCtaText() {
    if (
      this.confirmationPageCard &&
      this.confirmationPageCard.ctaText &&
      this.confirmationPageCard.ctaText.trim() !== ''
    ) {
      return this.confirmationPageCard.ctaText;
    } else {
      if (this.hasConfirmed()) {
        return this.translate.instant('APP.EVENT_RSVP.SEE_YOUR_QR_CODE');
      } else {
        return this.translate.instant('APP.CHANGE_PAGES.TO_THE_EVENT');
      }
    }
  }

  getTitle() {
    if (this.withTickets) {
      return this.translate.instant('APP.EVENT_AFTER_ATTEND.TICKETS.TITLE');
    }
    if (
      this.confirmationPageCard &&
      this.confirmationPageCard.title &&
      this.confirmationPageCard.title.trim() !== ''
    ) {
      return this.confirmationPageCard.title;
    } else {
      if (this.withSlots) {
        return this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.GENERIC.YOU_ARE_SUCCESSFULLY_REGISTERED',
        );
      }
      switch (this.rsvpType) {
        case RSVPOptionType.YES:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.YOU_ARE_SUCCESSFULLY_REGISTERED',
          );
        case RSVPOptionType.NO:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_UPDATE',
          );
        case RSVPOptionType.MAYBE:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_UPDATE',
          );
        case RSVPOptionType.WAITLIST:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_RESPONSE',
          );
        case RSVPOptionType.PENDING_APPROVAL:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.AWAITING_HOST_APPROVAL',
          );
        case RSVPOptionType.WITHDRAW:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.THANKS_FOR_UPDATE',
          );
        default:
          return '';
      }
    }
  }

  findCardByType() {
    if (
      !this.event ||
      !this.event.confirmationPageCards ||
      this.event.confirmationPageCards.length === 0
    )
      return;
    let card = undefined;
    const pageCards = this.event.confirmationPageCards;
    if (this.withSlots) {
      card = pageCards.find(
        (x) => x.type === EventConfirmationPageCardType.BOOK_A_SLOT,
      );
    } else {
      switch (this.rsvpType) {
        case RSVPOptionType.YES:
          card = pageCards.find(
            (x) => x.type === EventConfirmationPageCardType.YES,
          );
          break;
        case RSVPOptionType.NO:
          card = pageCards.find(
            (x) => x.type === EventConfirmationPageCardType.NO,
          );
          break;
        case RSVPOptionType.MAYBE:
          card = pageCards.find(
            (x) => x.type === EventConfirmationPageCardType.MAYBE,
          );
          break;
        case RSVPOptionType.WAITLIST:
          card = pageCards.find(
            (x) => x.type === EventConfirmationPageCardType.WAITLIST,
          );
          break;
        case RSVPOptionType.PENDING_APPROVAL:
          card = pageCards.find(
            (x) => x.type === EventConfirmationPageCardType.PENDING,
          );
          break;
      }
    }
    return card;
  }

  toTheEvent() {
    if (!this.event) return;
    this.router.navigate(['/', AppRoutes.Events.events, this.event.uri]);
    this.onClose();
  }

  getDescription() {
    if (this.withTickets) {
      return this.translate.instant(
        'APP.EVENT_AFTER_ATTEND.TICKETS.DESCRIPTION',
        {
          eventName: this.event?.name,
        },
      );
    }
    if (
      this.confirmationPageCard &&
      this.confirmationPageCard.description &&
      this.confirmationPageCard.description.trim() !== ''
    ) {
      return this.confirmationPageCard.description;
    } else {
      switch (this.rsvpType) {
        case RSVPOptionType.YES:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.YES_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.NO:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.NO_DESCRIPTION',
          );
        case RSVPOptionType.MAYBE:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.MAYBE_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.WAITLIST:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.WAIT_LIST_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.PENDING_APPROVAL:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.PENDING_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        case RSVPOptionType.WITHDRAW:
          return this.translate.instant(
            'APP.EVENT_AFTER_ATTEND.GENERIC.WITHDRAW_DESCRIPTION',
            {
              eventName: this.event?.name,
            },
          );
        default:
          return '';
      }
    }
  }

  seeRsvpQRCode() {
    this.dialog.open(RsvpQrCodeDialog, {
      viewContainerRef: this.viewContainerRef,
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        attendee: this.attendee,
      },
      panelClass: ['normal-dialog', 'secondary-dialog'],
      backdropClass: '',
    });
  }

  addToWallet() {
    if (
      !this.event ||
      !this.attendee?.uuid ||
      this.actionInProgress === 'addToWallet'
    ) {
      return;
    }

    this.actionInProgress = 'addToWallet';

    const windowReference = window.open('about:blank', '_blank');

    this.eventService
      .getEventRsvpWalletPass(this.event.id, this.attendee.uuid)
      .pipe(finalize(() => (this.actionInProgress = undefined)))
      .subscribe({
        next: (passPageLink: string) => {
          if (windowReference) {
            windowReference.location.href = passPageLink;
          }
        },
        error: () => {
          if (windowReference) {
            windowReference.close();
          }
          this.notificationService.error(
            this.translate.instant('APP.EVENT_PAYMENT.COULD_NOT_DOWNLOAD'),
          );
        },
      });
  }

  protected readonly EventRSVPConfirmationPageSectionType =
    EventRSVPConfirmationPageSectionType;
  protected readonly RSVPOptionType = RSVPOptionType;
  protected readonly EventWidget = EventWidget;
}
